import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, Subject, take, throwError } from 'rxjs';
import { map, concatMap, share, switchMap, tap, catchError } from 'rxjs/operators';
import { AuthenticationService } from './AuthenticationService';
import { MoovManagementApiService } from './MoovManagementApiService';
import { BasicResponse } from '../responses/basicResponse';
import { User } from '../models/user';
import { MoovDefinition } from '../models/moovDefinition';
import { MoovDefinitionResponse } from '../responses/moovDefinitionResponse';
import { MoovStepDefinition } from '../models/moovStepDefinition';
import { MoovStepDefinitionResponse } from '../responses/moovStepDefinitionResponse';
import { MoovConfiguration } from '../models/moovConfiguration';
import { MoovConfigurationResponse } from '../responses/moovConfigurationResponse';
import { MoovExecutionHistoryResponse } from '../responses/moovExecutionHistoryResponse';
import { MoovInstanceExecutionHistory } from '../models/moovInstanceExecutionHistory';
import { UploadFileResponse } from '../responses/uploadFileResponse';
import { MoovInstance } from '../models/moovInstance';
import { MoovInstanceResponse } from '../responses/moovInstanceResponse';
import { MoovStepInstance } from '../models/moovStepInstance';
import { MoovStepCustomerParameters } from '../models/moovStepCustomerParameters';
import { MoovVariable } from '../models/moovVariable';
import { MoovVariableResponse } from '../responses/moovVariableResponse';
import { ActionItem } from '../models/actionItem';
import { ActionItemResponse } from '../responses/actionItemResponse';
import { Router } from '@angular/router';
import { ReceivedFile } from '../models/receivedFile';
import { ReceivedFileResponse } from '../responses/receivedFileResponse';
import { MoovInstanceAbstract } from '../models/moovInstanceAbstract';
import { ProcessingMoovInstancesResponse } from '../responses/processingMoovInstancesResponse';
import { ExecutedMoovInstance } from '../models/executedMoovInstance';
import { ExecutedMoovInstancesResponse } from '../responses/executedMoovInstancesResponse';
import { Customer } from '../models/customer';
import { BillableMoovData } from '../models/billableMoovData';
import { BillableMoovDataResponse } from '../responses/billableMoovDataResponse';
import { SearchFile } from '../models/searchFile';
import { SearchFileResponse } from '../responses/searchFileResponse';
import { ActiveMoovDefinitionsCountResponse } from '../responses/activeMoovDefinitionsCountResponse';
import { ActiveMoovConfigurationsCountResponse } from '../responses/activeMoovConfigurationsCountResponse';
import { PremierServiceSchedule } from '../models/premierServiceSchedule';
import { PremierServiceExecution } from '../models/premierServiceExecution';
import { PremierServiceExecutionsResponse } from '../responses/premierServiceExecutionsResponse';
import { TimeZone } from '../models/timeZone';
import { ScheduledPremierService } from '../models/scheduledPremierService';
import { ScheduledPremierServicesResponse } from '../responses/scheduledPremierServicesResponse';
import { MoovDefinitionDTO } from '../DTO/moovDefinitionDTO';
import { MoovConfigurationDTO } from '../DTO/moovConfigurationDTO';
import { MoovStepCustomerParametersResponse } from '../responses/moovStepCustomerParametersResponse';
import { MoovVariableDTO } from '../DTO/moovVariableDTO';
import { MoovConfigurationStatus } from '../models/moovConfigurationStatus';
import { MoovConfigurationStatusResonse } from '../responses/moovConfigurationStatusResponse';
import { ExternalFileUploadInfoResponse } from '../responses/externalFileUploadInformationResponse';
import { MoovDefinitionCustomerAdoption } from '../models/moovDefinitionCustomerAdoption';
import { MoovDefinitionCustomerAdoptionDTO } from '../DTO/moovDefinitionCustomerAdoptionDTO';
import { MoovDefinitionCustomerAdoptionResponse } from '../responses/moovDefinitionCustomerAdoptionResponse';
import { LoggingService } from './LoggingService';
import { TimeZoneDTO } from '../DTO/timeZoneDTO';
import { MoovStep } from '../models/moovStep';
import { AppConfig } from "../app.config";
import { MoovInstancesResponse } from '../responses/moovInstancesResponse';
import { doesNotThrow } from 'assert';

@Injectable({ providedIn: 'root' })
export class MoovService {

    private moovStepDefinitionsObservableCache?: Observable<MoovStepDefinition[]>;
    private moovStepDefinitionsCache?: MoovStepDefinition[];

    private moovDefinitionsObservableCache?: Observable<MoovDefinition[]>;
    private moovDefinitionByIdObservableCache: { [moovDefinitionId: number]: Observable<MoovDefinition | undefined> | undefined } = {};
    private moovDefinitionsCache?: MoovDefinition[];

    private adoptedMoovDefinitionsObservableCache?: Observable<MoovDefinition[]>;
    private adoptedMoovDefinitionsCache?: MoovDefinition[];

    private moovConfigurationsObservableCache: { [moovDefinitionId: number]: Observable<MoovConfiguration[]> | undefined } = {};
    private moovConfigurationsCache: { [moovDefinitionId: number]: MoovConfiguration[] } = {};

    private moovConfigurationsForClientObservableCache: {[clientId: number]: Observable<MoovConfiguration[]> | undefined} = {};
    private moovConfigurationsForClientCache: {[clientId: number]: MoovConfiguration[] | undefined } = {};

    private moovConfigurationStatusReportObservableCache? : Observable<MoovConfigurationStatus[]>;

    private moovInstanceExecutionHistoryObservableCache: { [moovDefinitionId: number]: Observable<MoovInstanceExecutionHistory[]> | undefined } = {};
    private moovInstanceExecutionHistoryCache: { [moovDefinitionId: number]: MoovInstanceExecutionHistory[] | undefined } = {};

    private processingMoovInstancesObservableCache?: Observable<MoovInstanceAbstract[]>;
    private processingMoovInstancesCache?: MoovInstanceAbstract[];

    private moovInstancesObservableCache: { [moovInstanceId: number]: Observable<MoovInstance | undefined> } = {};
    private moovInstancesCache: { [moovInstanceId: number]: MoovInstance } = {};

    private moovInstanceCollisionsObservableCache: { [collisionId: string]: Observable<MoovInstance[]> } = {};
    private moovInstanceCollisionsCache: { [collisionId: string]: MoovInstance[] } = {};

    public newMoovInstanceSubject = new Subject<{ response: UploadFileResponse, files: File[] }>();

    private moovVariablesObservableCache?: Observable<MoovVariable[]>;
    private moovVariablesCache?: MoovVariable[];

    private actionItemsObservableCache?: Observable<ActionItem[]>;
    private actionItemsCache?: ActionItem[];

    private actionItemsCountObservableCache?: Observable<{ count: number, timestamp: Date }>;
    private actionItemsCountCache: { count: number, timestamp: Date } = {count:0, timestamp:new Date(0)};

    private receivedFilesObservableCache?: Observable<ReceivedFile[]>;

    private executedMoovInstancesObservableCache?: Observable<ExecutedMoovInstance[]>;

    private billableMoovDataObservableCache?: Observable<BillableMoovData[]>;

    private searchFileObservableCache?: Observable<SearchFile[]>;

    private activeMoovDefinitionsCountObservableCache?: Observable<number|undefined>;
    private activeMoovDefinitionsCountCache: number = 0;

    private activeMoovConfigurationsCountObservableCache?: Observable<number|undefined>;
    private activeMoovConfigurationsCountCache:number = 0;

    public actionItemCountChangedSubject = new Subject<any>();

    private premierServiceExecutionsObservableCache?: Observable<PremierServiceExecution[]>;

    private scheduledPremierServicesObservableCache?: Observable<ScheduledPremierService[]>;

    public currentMoovConfigurationClone?:MoovConfiguration;

    constructor(private mmApiService: MoovManagementApiService, private authService: AuthenticationService, private router: Router, private logger:LoggingService, private appConfig:AppConfig) {
        this.authService.currentUserSubject.subscribe(au => this.processCurrentUser(au));
        this.authService.currentCustomerContextSubject.subscribe(customer => this.processCurrentCustomerContext(customer));
    }

    get authorized() {
        return this.authService.authorizedSubject.value;
    }

    get currentUser() {
        return this.authService.currentUserValue;
    }

    private processCurrentUser(u: User|undefined) {
        if (!u) {
            //user logged out, clear all caches
            this.clearCache();
        }
    }

    private processCurrentCustomerContext(customer: Customer|undefined) {
        this.clearCache();
    }

    private clearCache() {
        this.moovStepDefinitionsCache = undefined;
        this.moovDefinitionsCache = undefined;
        this.adoptedMoovDefinitionsCache = undefined;
        this.moovConfigurationsCache = {};
        this.moovConfigurationsForClientCache = {};
        this.moovInstanceExecutionHistoryCache = {};
        this.processingMoovInstancesCache = undefined;
        this.moovInstancesCache = {};
        this.moovInstanceCollisionsCache = {};
        this.moovVariablesCache = undefined;
        this.actionItemsCache = undefined;
        this.actionItemsCountCache = {count:0, timestamp:new Date(0)};
        this.activeMoovDefinitionsCountCache = 0;
        this.activeMoovConfigurationsCountCache = 0;
    }

    //#region moov execution status
    public getMoovExecutionStatus(){
        return this.mmApiService.getMoovExecutionStatus(this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => r), share());
    }

    public toggleMoovExecutionStatus(executingMoovs:boolean):Observable<any>{
        return this.mmApiService.toggleMoovExecutionStatus(this.authService.currentUserValue!.id, executingMoovs);
    }
    //#endregion
  

    //#region moov definition handling
    public getMoovStepDefinitions(forceReload: boolean) {
        if (forceReload) this.moovStepDefinitionsCache = undefined;
        if (!this.authorized) return of([]);
        // Data available
        if (this.moovStepDefinitionsCache) return of(this.moovStepDefinitionsCache);
        // Request pending
        else if (this.moovStepDefinitionsObservableCache) return this.moovStepDefinitionsObservableCache;
        // New request needed
        else this.moovStepDefinitionsObservableCache = this.fetchMoovStepDefinitions();
        return this.moovStepDefinitionsObservableCache;
    }

    private fetchMoovStepDefinitions() {
        return this.mmApiService.getMoovStepDefinitions(this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => this.processMoovStepDefinitions(r)), share());
    }

    private processMoovStepDefinitions(r: MoovStepDefinitionResponse) {
        this.moovStepDefinitionsObservableCache = undefined;
        this.moovStepDefinitionsCache = [];
        if (r.success) {
            r.moovStepDefinitions?.forEach(dto => {
                var msd = MoovStepDefinition.mapFromDTO(dto)
                this.moovStepDefinitionsCache!.push(msd);
            });
        }
        else {
            this.handleError('processMoovStepDefinitions', r.message);
        }
        return this.moovStepDefinitionsCache;
    }

    public getMoovStepDefinitionById(id: number, forceReload: boolean) {
        return this.getMoovStepDefinitions(forceReload).pipe(map(msd => msd!.find(msd => msd.id == id)));
    }

    public getMoovDefinitions(forceReload: boolean) {
        if (forceReload) this.moovDefinitionsCache = undefined;
        if (!this.authorized) return of([]);
        // Data available
        if (this.moovDefinitionsCache) return of(this.moovDefinitionsCache);
        // Request pending
        else if (this.moovDefinitionsObservableCache) return this.moovDefinitionsObservableCache;
        // New request needed
        else this.moovDefinitionsObservableCache = this.fetchMoovDefinitions();

        return this.moovDefinitionsObservableCache;
    }

    private fetchMoovDefinitions() {
        //7/10/2024: changed this to make sure moovStepDefinitions are loaded before we load moov definitions due to potential filtering problems when loading av adoptions into the UI
        return this.getMoovStepDefinitions(false).pipe(concatMap(msdArray=>this.mmApiService.getMoovDefinitions(this.authService.entityContextId,this.authService.entityContextType).pipe(switchMap((r) => this.processMoovDefinitions(r)))), share());
    }

    private processMoovDefinitions(r: MoovDefinitionResponse): Observable<MoovDefinition[]> {
        if (!r.success) {
            this.handleError('processMoovDefinitions', r.message);
            return of([]);
        }
        return forkJoin(
            r.moovDefinitions.map(dto => this.processMoovDefinitionDTOAsync(dto))
        ).pipe(
            tap(mds =>{
                this.moovDefinitionsCache = mds;
                this.moovDefinitionsObservableCache = undefined;
            })
        );
    }

    /** Emits the definition only once every step's `moovStepDefinition` has been attached. */
    private processMoovDefinitionDTOAsync(dto: MoovDefinitionDTO): Observable<MoovDefinition> {
        const md = MoovDefinition.mapFromDTO(dto);
        if (md.steps.length === 0) return of(md);
        return forkJoin(
            md.steps.map(s =>
            this.getMoovStepDefinitionById(s.moovStepDefinitionId, false).pipe(
                take(1),
                //it could be that the customer lost access to certain premier partner related step definitions if the premier partner was removed => dummy
                tap(msd => { s.moovStepDefinition = msd ?? new MoovStepDefinition(); }),
                catchError(() => { s.moovStepDefinition = new MoovStepDefinition(); return of(undefined); })
            )
            )
        ).pipe(map(() => md));
    }

    public getMoovDefinitionById(id: number, forceReload: boolean) {
        //9/23/2022: switched from switchMap to concatMap
        return this.getMoovDefinitions(forceReload).pipe(concatMap(mdArray =>  (mdArray && mdArray.findIndex(md => md.id == id) > -1) ? of(mdArray.find(md => md.id == id)) : this.fetchMoovDefinitionById(id)),share());
    }

    private fetchMoovDefinitionById(id: number) {
        if (!this.moovDefinitionByIdObservableCache[id]) {
            this.moovDefinitionByIdObservableCache[id] = this.mmApiService.getMoovDefinitionById(id, this.authService.entityContextId,this.authService.entityContextType).pipe(switchMap((r) => this.processMoovDefinitionById(r)), share());
        }
        return this.moovDefinitionByIdObservableCache[id];
    }

    private processMoovDefinitionById(r: MoovDefinitionResponse): Observable<MoovDefinition | undefined> {
        if (!r.success) {
            this.handleError('processMoovDefinitionById', r.message);
            return of(undefined);
        }
        //only expecting one moov definition
        const dto = r.moovDefinitions?.[0];
        if (!dto) return of(undefined);
        return this.processMoovDefinitionDTOAsync(dto).pipe(tap(md => {
            delete this.moovDefinitionByIdObservableCache[dto.id!];
            this.moovDefinitionsCache?.push(md);
        }));
    }

    public getMoovDefinitionsById(idList: number[], forceReload: boolean) {
        //9/23/2022: switched from switchMap to concatMap
        return this.getMoovDefinitions(forceReload).pipe(concatMap((mdArray:MoovDefinition[]) => {
            var cachedMdArray:MoovDefinition[] = [];
            var nonCachedMdIdArray: number[] = [];
            if(mdArray){
                cachedMdArray = mdArray.filter(md => idList.includes(md.id!));
                nonCachedMdIdArray = idList.filter(id => !cachedMdArray.some(md => md.id === id));
            }
            if(nonCachedMdIdArray.length > 0){
                return this.fetchMoovDefinitionsById(nonCachedMdIdArray).pipe(map((mdArray) => {
                    return [...cachedMdArray, ...mdArray];
                }));
            }
            return of(cachedMdArray);
        }),share());
    }

    private fetchMoovDefinitionsById(idList: number[]) {
        return this.mmApiService.getMoovDefinitionsByIds(idList, this.authService.entityContextId,this.authService.entityContextType).pipe(switchMap((r) => this.processMoovDefinitionsById(r)), share());
    }

    private processMoovDefinitionsById(r: MoovDefinitionResponse): Observable<MoovDefinition[]> {
        if (!r.success) {
            this.handleError('processMoovDefinitionsById', r.message);
            return of([]);
        }
        const dtos = r.moovDefinitions ?? [];
        if (dtos.length === 0) return of([]);
        return forkJoin(dtos.map(dto => this.processMoovDefinitionDTOAsync(dto).pipe(take(1), tap(md => {
            delete this.moovDefinitionByIdObservableCache[dto.id!];
            this.moovDefinitionsCache?.push(md);
        }))));
    }

    public getRecommendedMoovDefinitions() {
        //only return recommendations if they are not already adopted.
        //if moovdefinition has a premierpartner restriction, then only return those for which the customer has access
        return this.getMoovDefinitions(false).pipe(concatMap(mdArray => 
            this.getAdoptedMoovDefinitions(false).pipe(map(amdArray => {
                return mdArray!.filter(md => md.published && !md.deleted && amdArray!.findIndex(amd => amd.id == md.id) == -1 && (!md.premierPartnerRestriction || md.premierPartnerRestriction==0 || this.authService.customerContext?.premierPartnerIdList.includes(md.premierPartnerRestriction)));
        }))), share());

    }

    public getAdoptedMoovDefinitions(forceReload: boolean) {
        if (forceReload) this.adoptedMoovDefinitionsCache = undefined;
        if (!this.authorized) return of([]);
        // Data available
        if (this.adoptedMoovDefinitionsCache) return of(this.adoptedMoovDefinitionsCache);
        // Request pending
        else if (this.adoptedMoovDefinitionsObservableCache) return this.adoptedMoovDefinitionsObservableCache;
        // New request needed
        else this.adoptedMoovDefinitionsObservableCache = this.fetchAdoptedMoovDefinitions();

        return this.adoptedMoovDefinitionsObservableCache;
    }

    private fetchAdoptedMoovDefinitions() {
        //7/10/2024: changed this to make sure moovStepDefinitions are loaded before we load moov definitions due to potential filtering problems when loading vailabla adoptions into the UI
        return this.getMoovStepDefinitions(false).pipe(concatMap(msdArray=> this.mmApiService.getAdoptedMoovDefinitions(this.authService.entityContextId,this.authService.entityContextType).pipe(switchMap((r) => this.processAdoptedMoovDefinitions(r)))), share());
    }

    private processAdoptedMoovDefinitions(r: MoovDefinitionResponse): Observable<MoovDefinition[]> {
        if (!r.success) {
            this.handleError('processAdoptedMoovDefinitions', r.message);
            this.adoptedMoovDefinitionsCache = [];
            this.adoptedMoovDefinitionsObservableCache = undefined;
            return of([]);
        }
        const dtos = r.moovDefinitions ?? [];
        if (dtos.length === 0) {
            this.adoptedMoovDefinitionsCache = [];
            this.adoptedMoovDefinitionsObservableCache = undefined;
            return of([]);
        }
        return forkJoin(dtos.map(dto => this.processMoovDefinitionDTOAsync(dto).pipe(take(1)))).pipe(
            tap(list => {
                this.adoptedMoovDefinitionsCache = list;
                this.adoptedMoovDefinitionsObservableCache = undefined;
            })
        );
    }

    public getAdoptedMoovDefinitionById(id: number, forceReload: boolean) {
        //there could be a rare situation where we need an adopted moov definition which is of status deleted, so it is not loaded
        //we would need to force load an individual one.
        return this.getAdoptedMoovDefinitions(forceReload).pipe(concatMap(mdArray =>{
            var md=mdArray!.find(md => md.id == id);
            if(md) return of(md);
            else{
                //force load a deleted moovdefinition
                return this.getMoovDefinitionById(id,forceReload).pipe(take(1), map(md=>{return md}));
            }
        }));
    }

    public activateAdoptedMoovDefinition(id: number, active: boolean) {
        return this.mmApiService.activateAdoptedMoovDefinition(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, id, active).pipe(map((r: BasicResponse) => {
            if (r.success) this.processActivateAdoptedMoovDefinitions();
            return r;
        }), share());
    }

    private processActivateAdoptedMoovDefinitions() {
        this.getAdoptedMoovDefinitions(true);
    }

    public getMoovDefinitionCustomerAdoption(moovDefinitionId: number) {
        return this.mmApiService.getMoovDefinitionCustomerAdoption(moovDefinitionId, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r: MoovDefinitionCustomerAdoptionResponse)=>{
            if(r.success){
                return this.processMoovDefinitionCustomerAdoptionDTO(r.moovDefinitionCustomerAdoption!);
            }
            else return undefined;
        }));
    }

    private processMoovDefinitionCustomerAdoptionDTO(dto:MoovDefinitionCustomerAdoptionDTO):MoovDefinitionCustomerAdoption{
        var mdca = MoovDefinitionCustomerAdoption.mapFromDTO(dto);
        return mdca;
    }

    public updateMoovDefinitionCustomerAdoption(mdca: MoovDefinitionCustomerAdoption) {
        return this.mmApiService.updateMoovDefinitionCustomerAdoption(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, mdca);
    }

    public createMoovDefinition(md: MoovDefinition) {
        return this.mmApiService.createMoovDefinition(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, md).pipe(switchMap((r: MoovDefinitionResponse) => {
            if (!r.success) return of(r);
            return this.processCreateMoovDefinition(r).pipe(map(() => r));
        }), share());
    }
    private processCreateMoovDefinition(r: MoovDefinitionResponse): Observable<MoovDefinition | null> {
        const dto = r.moovDefinitions?.[0];
        if(!dto){
            this.handleError('processCreateMoovDefinition', r.message);
            return of(null);
        }
        return this.processMoovDefinitionDTOAsync(dto).pipe(tap(md => this.moovDefinitionsCache?.push(md)));
    }

    public updateMoovDefinition(md: MoovDefinition) {
        return this.mmApiService.updateMoovDefinition(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, md).pipe(switchMap((r: MoovDefinitionResponse) => {
            if (!r.success) return of(r);
            return this.processUpdateMoovDefinition(r, md).pipe(map(() => r));
        }), share());
    }
    private processUpdateMoovDefinition(r: MoovDefinitionResponse, old_md:MoovDefinition): Observable<MoovDefinition | null> {
        const dto = r.moovDefinitions?.[0];
        if(!dto){
            this.handleError('processUpdateMoovDefinition', r.message);
            return of(null);
        }
        //we might get a total new moov definition with a new id
        return this.processMoovDefinitionDTOAsync(dto).pipe(tap(md => {
            const index = this.moovDefinitionsCache?.findIndex(m=>m.id===old_md.id) ?? -1;
            if(index>-1){
                this.moovDefinitionsCache![index]=md;
                //in this case we might have moov configurations cached which point to the old moov definition => empty the related cache
                delete this.moovConfigurationsCache[old_md.id!];
            }
            else{
                this.moovDefinitionsCache?.push(md);
            }
            //if we update a moov definition it does not come back with the customer configuration parameters, so we need to clear the adoption cache
            this.adoptedMoovDefinitionsCache=undefined;
        }));
    }

    public deleteMoovDefinition(md: MoovDefinition) {
        return this.mmApiService.deleteMoovDefinition(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, md.id!).pipe(map((r: BasicResponse) => {
            if (r.success) this.processDeleteMoovDefinition(md);
            return r;
        }), share());
    }
    private processDeleteMoovDefinition(md: MoovDefinition) {
        md.deleted = true;
        const index = this.adoptedMoovDefinitionsCache?.findIndex(amd=>amd.id===md.id) ?? -1;
        if(index >-1){
            this.adoptedMoovDefinitionsCache![index].deleted=true;
        }
    }

    public undeleteMoovDefinition(md: MoovDefinition) {
        return this.mmApiService.undeleteMoovDefinition(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, md.id!).pipe(map((r: BasicResponse) => {
            if (r.success) this.processUndeleteMoovDefinition(md);
            return r;
        }), share());
    }

    private processUndeleteMoovDefinition(md: MoovDefinition) {
        md.deleted = false;
        let index = this.adoptedMoovDefinitionsCache?.findIndex(amd=>amd.id===md.id) ?? -1;
        if(index >-1){
            this.adoptedMoovDefinitionsCache![index].deleted=false;
        }
    }

    public publishMoovDefinition(md: MoovDefinition, published: boolean) {
        return this.mmApiService.publishMoovDefinition(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, md.id!, published).pipe(map((r: BasicResponse) => {
            return this.processPublishMoovDefinition(r, md, published);
        }), share());
    }

    private processPublishMoovDefinition(r: BasicResponse, md: MoovDefinition, published: boolean) {
        if (r.success) {
            md.published = published;
            if (md.ownedByEntityId) this.adoptedMoovDefinitionsCache = undefined; //a newly published definition becomes adopted if it is a customer owned definition
        }
        return r;
    }

    public testMoovStep(ms:MoovStep){
        return this.mmApiService.testMoovStep(ms);
    }

    public adoptMoovDefinition(md: MoovDefinition) {
        return this.mmApiService.adoptMoovDefinition(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, md.id!).pipe(switchMap((r: MoovDefinitionResponse) => {
            if (!r.success) return of(r);
            return this.processAdoptMoovDefinition(r).pipe(map(() => r));
        }), share());
    }
    private processAdoptMoovDefinition(r: MoovDefinitionResponse): Observable<MoovDefinition | null> {
        const dto = r.moovDefinitions?.[0];
        if(!dto){
            this.handleError('processAdoptMoovDefinition', r.message);
            return of(null);
        }
        return this.processMoovDefinitionDTOAsync(dto).pipe(tap(m => {
            if(this.adoptedMoovDefinitionsCache){
                const index = this.adoptedMoovDefinitionsCache.findIndex(md=>md.id==m.id);
                if(index>-1) this.adoptedMoovDefinitionsCache[index]=m;
                else this.adoptedMoovDefinitionsCache.push(m);
            }
        }));
    }

    public abandonMoovDefinition(md: MoovDefinition) {
        return this.mmApiService.abandonMoovDefinition(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, md.id!).pipe(map((r: BasicResponse) => {
            if (r.success) this.processAbandonMoovDefinition(md);
            return r;
        }), share());
    }
    private processAbandonMoovDefinition(md: MoovDefinition) {
        let index = this.adoptedMoovDefinitionsCache!.indexOf(md);
        if (index > -1) {
            this.adoptedMoovDefinitionsCache!.splice(index, 1);
        }
    }

    public createMoovStepCustomerParameters(mscp: MoovStepCustomerParameters, moovDefinition: MoovDefinition) {
        return this.mmApiService.createMoovStepCustomerParameters(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, mscp).pipe(map((r: MoovStepCustomerParametersResponse) => {
            if (r.success) this.processCreateMoovStepCustomerParameters(r, moovDefinition);
            return r;
        }), share());
    }
    private processCreateMoovStepCustomerParameters(r: MoovStepCustomerParametersResponse, moovDefinition: MoovDefinition) {
       delete this.moovConfigurationsCache[moovDefinition.id!];
    }

    public updateMoovStepCustomerParameters(mscp: MoovStepCustomerParameters, moovDefinition: MoovDefinition) {
        return this.mmApiService.updateMoovStepCustomerParameters(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, mscp).pipe(map((r: MoovStepCustomerParametersResponse) => {
            if (r.success) this.processUpdateMoovStepCustomerParameters(r, moovDefinition);
            return r;
        }), share());
    }
    private processUpdateMoovStepCustomerParameters(r: MoovStepCustomerParametersResponse, moovDefinition: MoovDefinition) {
        delete this.moovConfigurationsCache[moovDefinition.id!];
    }

    public deleteMoovStepCustomerParameters(mscp: MoovStepCustomerParameters, moovDefinition: MoovDefinition) {
        return this.mmApiService.deleteMoovStepCustomerParameters(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, mscp.id).pipe(map((r: BasicResponse) => {
            if (r.success) this.processDeleteMoovStepCustomerParameters(mscp, moovDefinition);
            return r;
        }), share());
    }
    private processDeleteMoovStepCustomerParameters(mscp: MoovStepCustomerParameters, moovDefinition: MoovDefinition) {
        delete this.moovConfigurationsCache[moovDefinition.id!];
    }

    public updateMoovConfigurationUsagePriceOverride(moovConfigurationId:number, usagePriceOverride:number){
        return this.mmApiService.updateMoovConfigurationUsagePriceOverride(this.authService.currentUserValue!.id,this.authService.entityContextId,this.authService.entityContextType, moovConfigurationId, usagePriceOverride).pipe(map((r: BasicResponse) => {
            return r;
        }), share());
    }

    public getActiveMoovDefinitionsCount(forceReload: boolean) {
        if (forceReload) this.activeMoovDefinitionsCountCache = 0;
        if (!this.authorized) return of(0);
        if (this.activeMoovDefinitionsCountCache! > 0) return of(this.activeMoovDefinitionsCountCache);
        else if (this.activeMoovDefinitionsCountObservableCache) return this.activeMoovDefinitionsCountObservableCache;
        else this.activeMoovDefinitionsCountObservableCache = this.fetchActiveMoovDefinitionsCount();
        return this.activeMoovDefinitionsCountObservableCache;
    }

    private fetchActiveMoovDefinitionsCount() {
        return this.mmApiService.getActiveMoovDefinitionsCount().pipe(map((r) => this.processActiveMoovDefinitionsCount(r)), share());
    }

    private processActiveMoovDefinitionsCount(r: ActiveMoovDefinitionsCountResponse) {
        this.activeMoovDefinitionsCountObservableCache = undefined;
        this.activeMoovDefinitionsCountCache = 0;
        if (r.success && r.longCode) {
            this.activeMoovDefinitionsCountCache = r.longCode;
        }
        else {
            this.handleError('processActiveMoovDefinitionsCount', r.message);
        }
        return this.activeMoovDefinitionsCountCache;
    }
    //#endregion

    //#region moov configuration handling
    public getMoovConfigurations(forceReload: boolean, moovDefinitionId: number) {
        if (forceReload) delete this.moovConfigurationsCache[moovDefinitionId];
        if (!this.authorized) return of([]);
        // Data available
        if (this.moovConfigurationsCache[moovDefinitionId]) return of(this.moovConfigurationsCache[moovDefinitionId]);
        // Request pending
        else if (this.moovConfigurationsObservableCache[moovDefinitionId]) return this.moovConfigurationsObservableCache[moovDefinitionId];
        // New request needed
        else this.moovConfigurationsObservableCache[moovDefinitionId] = this.fetchMoovConfigurations(moovDefinitionId);

        return this.moovConfigurationsObservableCache[moovDefinitionId];
    }

    private fetchMoovConfigurations(moovDefinitionId: number) {
        return this.mmApiService.getMoovConfigurations(moovDefinitionId, this.authService.entityContextId,this.authService.entityContextType).pipe(switchMap((r) => this.processMoovConfigurations(r, moovDefinitionId)), share());
    }

    private processMoovConfigurations(r: MoovConfigurationResponse, moovDefinitionId: number): Observable<MoovConfiguration[]> {
        if (!r.success) {
            this.handleError('processMoovConfigurations', r.message);
            this.moovConfigurationsCache[moovDefinitionId] = [];
            delete this.moovConfigurationsObservableCache[moovDefinitionId];
            return of([]);
        }
        const dtos = r.moovConfigurations ?? [];
        if (dtos.length === 0) {
            this.moovConfigurationsCache[moovDefinitionId] = [];
            delete this.moovConfigurationsObservableCache[moovDefinitionId];
            return of([]);
        }
        return forkJoin(dtos.map(dto => this.processMoovConfigurationDTO(dto).pipe(take(1)))).pipe(
            tap(list => {
                this.moovConfigurationsCache[moovDefinitionId] = list;
                delete this.moovConfigurationsObservableCache[moovDefinitionId];
            })
        );
    }

    /** Emits the configuration only once `moovDefinition` (fully hydrated) and each step config's `moovStep` are wired. */
    private processMoovConfigurationDTO(dto:MoovConfigurationDTO):Observable<MoovConfiguration>{
        const mc = MoovConfiguration.mapFromDTO(dto);
        return this.getAdoptedMoovDefinitionById(mc.moovDefinitionId, false).pipe(
            take(1),
            map(md => {
                mc.moovDefinition = md!;
                mc.stepConfigurations?.forEach(sc => {
                    sc.moovStep = mc.moovDefinition.steps.find(s => s.id == sc.moovStepId)!;
                });
                return mc;
            })
        );
    }

    public getMoovConfigurationById(id: number, moovDefinitionId: number) {
        //at the moment we have to force reload as we might look at a configuration with updated premier service parameters
        return this.getMoovConfigurations(true, moovDefinitionId).pipe(map(mc => mc.find(mc => mc.id == id)));
    }

    public getMoovConfigurationsForClient(forceReload:boolean, clientId:number){
        if(forceReload) delete this.moovConfigurationsForClientCache[clientId];
        if(!this.authorized) return of([]);
        // Data available
        if(this.moovConfigurationsForClientCache[clientId]) return of(this.moovConfigurationsForClientCache[clientId]);
        // Request pending
        else if (this.moovConfigurationsForClientObservableCache[clientId]) return this.moovConfigurationsForClientObservableCache[clientId];
        // New request needed
        else this.moovConfigurationsForClientObservableCache[clientId] = this.fetchMoovConfigurationsForClient(clientId);

        return this.moovConfigurationsForClientObservableCache[clientId];
    }

    private fetchMoovConfigurationsForClient(clientId: number){
        return this.mmApiService.getMoovConfigurationsForClient(clientId, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => this.processMoovConfigurationsForClient(r, clientId)), share());
    }

    private processMoovConfigurationsForClient(r: MoovConfigurationResponse, clientId: number){
        delete this.moovConfigurationsForClientObservableCache[clientId];
        let mcArray = new Array<MoovConfiguration>();
        if(r.success){
            r.moovConfigurations?.forEach(dto => {
                let mc = MoovConfiguration.mapFromDTO(dto);
                mcArray.push(mc);
            });
            this.moovConfigurationsForClientCache[clientId] = mcArray;
        }
        else{
            this.handleError('processMoovConfigurationsForClient', r.message);
        }
        return mcArray;
    }

    public getMoovConfigurationsMatchingName(filename: string){
        return this.mmApiService.getMoovConfigurationsMatchingName(filename, this.authService.entityContextId,this.authService.entityContextType).pipe(switchMap((r) => this.processMoovConfigurationsMatchingName(r)), share());
    }

    private processMoovConfigurationsMatchingName(r: MoovConfigurationResponse): Observable<MoovConfiguration[]>{
        if(!r.success){
            this.handleError('processMoovConfigurationsMatchingName', r.message);
            return of([]);
        }
        const dtos = r.moovConfigurations ?? [];
        if(dtos.length === 0) return of([]);
        return forkJoin(dtos.map(dto => this.processMoovConfigurationDTO(dto).pipe(take(1))));
    }

    public getExternalFileUploadInformation(uniqueIdentifier: string) {
        return this.mmApiService.getExternalFileUploadInformation(uniqueIdentifier).pipe(map((r:ExternalFileUploadInfoResponse) => {
            this.logger.log(r);
            if (r.success) {
                if (r.moovConfiguration) {
                    let mc = MoovConfiguration.mapFromDTO(r.moovConfiguration);
                    return { companyName: r.companyName, brandingImageURL: r.brandingImageURL, brandName: r.brandName, businessDisplayName: r.businessDisplayName, primaryColor: r.primaryColor, moovConfiguration: mc, clientName: r.clientName, hasQBOImportStep: r.hasQBOImportStep, hasXeroImportStep: r.hasXeroImportStep, hasDropboxUploadStep:r.hasDropboxUploadStep,                     hasMicrosoftImportStep:r.hasMicrosoftImportStep, microsoftAppType:r.microsoftAppType, token:r.token};
                }
                else {
                                        this.handleCustomerError({ companyName: r.companyName, brandingImageURL: r.brandingImageURL, brandName: r.brandName, businessDisplayName: r.businessDisplayName, primaryColor: r.primaryColor, clientName: r.clientName, hasQBOImportStep: r.hasQBOImportStep, hasXeroImportStep: r.hasXeroImportStep, hasDropboxUploadStep:r.hasDropboxUploadStep, hasMicrosoftImportStep:r.hasMicrosoftImportStep, microsoftAppType:r.microsoftAppType, token:r.token});
                }
            }
            else {
                this.handleError('processExternalFileUploadInformation', r.message);
            }
        }), share());
    }

    public createMoovConfiguration(mc: MoovConfiguration) {
        return this.mmApiService.createMoovConfiguration(this.authService.currentUserValue!.id, mc).pipe(switchMap((r: MoovConfigurationResponse) => {
            if (!r.success) return of(r);
            return this.processCreateMoovConfiguration(r).pipe(map(() => r));
        }), share());
    }
    private processCreateMoovConfiguration(r: MoovConfigurationResponse): Observable<MoovConfiguration | null> {
        const dto = r.moovConfigurations?.[0];
        if(!dto){
            this.handleError('processCreateMoovConfiguration', r.message);
            return of(null);
        }
        return this.processMoovConfigurationDTO(dto).pipe(tap(mc => {
            if(!this.moovConfigurationsCache[mc.moovDefinitionId]) this.moovConfigurationsCache[mc.moovDefinitionId]=[];
            this.moovConfigurationsCache[mc.moovDefinitionId].push(mc);
        }));
    }

    public updateMoovConfiguration(mc: MoovConfiguration) {
        return this.mmApiService.updateMoovConfiguration(this.authService.currentUserValue!.id, mc).pipe(switchMap((r: MoovConfigurationResponse) => {
            if (!r.success) return of(r);
            return this.processUpdateMoovConfiguration(r).pipe(map(() => r));
        }), share());
    }
    public processUpdateMoovConfiguration(r: MoovConfigurationResponse): Observable<MoovConfiguration | null> {
        const dto = r.moovConfigurations?.[0];
        if(!dto){
            this.handleError('processUpdateMoovConfiguration', r.message);
            return of(null);
        }
        return this.processMoovConfigurationDTO(dto).pipe(tap(m => {
            const arr = this.moovConfigurationsCache[m.moovDefinitionId];
            if(arr){
                const index = arr.findIndex(mc=>mc.id==m.id);
                if(index>-1) arr[index]=m;
                else arr.push(m);
            }
        }));
    }

    public deleteMoovConfiguration(mc: MoovConfiguration) {
        return this.mmApiService.deleteMoovConfiguration(this.authService.currentUserValue!.id, mc).pipe(map((r: BasicResponse) => {
            if (r.success) this.processDeleteMoovConfiguration(mc);
            return r;
        }), share());
    }
    private processDeleteMoovConfiguration(mc: MoovConfiguration) {
        mc.deleted=true;
    }

    public undeleteMoovConfiguration(mc: MoovConfiguration) {
        return this.mmApiService.undeleteMoovConfiguration(this.authService.currentUserValue!.id, mc).pipe(map((r: BasicResponse) => {
            if (r.success) this.processUndeleteMoovConfiguration(mc);
            return r;
        }), share());;
    }
    private processUndeleteMoovConfiguration(mc: MoovConfiguration) {
        mc.deleted=false;
    }

    public getActiveMoovConfigurationsCount(forceReload:boolean){
        if(forceReload) this.activeMoovConfigurationsCountCache = 0;
        if(!this.authorized) return of(0);
        if(this.activeMoovConfigurationsCountCache > 0) return of(this.activeMoovConfigurationsCountCache);
        else if(this.activeMoovConfigurationsCountObservableCache) return this.activeMoovConfigurationsCountObservableCache;
        else this.activeMoovConfigurationsCountObservableCache = this.fetchActiveMoovConfigurationsCount();
        return this.activeMoovConfigurationsCountObservableCache;
    }

    private fetchActiveMoovConfigurationsCount(){
        return this.mmApiService.getActiveMoovConfigurationsCount().pipe(map((r)=> this.processActiveMoovConfigurationsCount(r)), share());
    }

    private processActiveMoovConfigurationsCount(r:ActiveMoovConfigurationsCountResponse){
        this.activeMoovConfigurationsCountObservableCache = undefined;
        this.activeMoovConfigurationsCountCache = 0;
        if(r.success && r.longCode){
            this.activeMoovConfigurationsCountCache = r.longCode;
        }
        else {
            this.handleError('processActiveMoovConfigurationsCount', r.message);
        }

        return this.activeMoovConfigurationsCountCache;
    }

    public getMoovConfigurationStatusReport(forcReload:boolean){
        if(forcReload) this.moovConfigurationStatusReportObservableCache = undefined;
        if(!this.authorized) return of([]);
        //Request pending
        if(this.moovConfigurationStatusReportObservableCache) return this.moovConfigurationStatusReportObservableCache;
        //New request needed
        else this.moovConfigurationStatusReportObservableCache = this.fetchMoovConfigurationStatusReport();
        return this.moovConfigurationStatusReportObservableCache;
    }

    private fetchMoovConfigurationStatusReport(){
        return this.mmApiService.getMoovConfigurationStatusReport(this.authService.entityContextId,this.authService.entityContextType).pipe(map((r)=> this.processMoovConfigurationSatusReport(r)), share());
    }

    private processMoovConfigurationSatusReport(r: MoovConfigurationStatusResonse){
        this.moovConfigurationStatusReportObservableCache = undefined;
        let moovConfigurationByStatus = new Array<MoovConfigurationStatus>(0);
        if(r.success){
            r.moovConfigurationStatusList?.forEach(dto =>{
                var mcs = MoovConfigurationStatus.mapFromDTO(dto);
                moovConfigurationByStatus.push(mcs);
            });
        }
        else {
            this.handleError('processMoovConfigurationStatusReport', r.message);
        }
        return moovConfigurationByStatus;
    }

    public getMoovConfigurationsInStatus(forceReload:boolean, status:number){
        if(forceReload) this.moovConfigurationStatusReportObservableCache = undefined;
        if(!this.authorized) return of([]);
        //Request pending
        if(this.moovConfigurationStatusReportObservableCache) return this.moovConfigurationStatusReportObservableCache;
        //New request needed
        else this.moovConfigurationStatusReportObservableCache = this.fetchMoovConfigurationsInStatus(status);
        return this.moovConfigurationStatusReportObservableCache;
    }

    private fetchMoovConfigurationsInStatus(status:number){
        return this.mmApiService.getMoovConfigurationsInStatus(status, this.authService.entityContextId, this.authService.entityContextType).pipe(map((r)=> this.processMoovConfigurationsInStatus(r)), share());
    }

    private processMoovConfigurationsInStatus(r: MoovConfigurationStatusResonse){
        this.moovConfigurationStatusReportObservableCache = undefined;
        let moovConfigurationByStatus = new Array<MoovConfigurationStatus>(0);
        if(r.success){
            r.moovConfigurationStatusList?.forEach(dto =>{
                var mcs = MoovConfigurationStatus.mapFromDTO(dto);
                moovConfigurationByStatus.push(mcs);
            });
        }
        else {
            this.handleError('processMoovConfigurationsInStatus', r.message);
        }
        return moovConfigurationByStatus;
    }

    public getMoovInformationForBillingBucket(billingBucketId:number) {
        return this.mmApiService.getMoovInformationForBillingBucket(billingBucketId);
    }
    //#endregion

    //#region moov instance handling
    public uploadFile(files: File[], addHeaderIfMissing: boolean) {
        return this.mmApiService.uploadFiles(files, addHeaderIfMissing).pipe(map((r) => {
            if (r.success) {
                this.newMoovInstanceSubject.next({ response: r, files: files });
                if (r.collision) {
                    this.getActionItemsCount(true).pipe(take(1)).subscribe();
                    if (r.collisionId) {
                        if (this.moovInstanceCollisionsCache[r.collisionId]) delete this.moovInstanceCollisionsCache[r.collisionId]; //clear cache and force reload
                    }
                }
            }
            return r;
        }));
    }

    public uploadFileFromExternal(files: File[], addHeaderIfMissing: boolean, externalUploaderName: string, externalUploaderEmail: string, moovConfigurationId: number, customerId: number) {
        return this.mmApiService.uploadFileFromExternal(files, addHeaderIfMissing, externalUploaderName, externalUploaderEmail, moovConfigurationId, customerId).pipe(map((r) => {
            if (r.success) {
                //
            }
            return r;
        }));
    }

    public reprocessFiles(fileIds:number[], filenameStartsWith:string, filenameContains:string) {
        return this.mmApiService.reprocessFiles(fileIds, filenameStartsWith, filenameContains).pipe(map((r) => {
            if (r.success) {
                //
            }
            return r;
        }));
    }

    public updateFile(file: File, moovFileId:number, moovStepInstanceId:number) {
        return this.mmApiService.updateFile(file, moovFileId, moovStepInstanceId).pipe(map((r) => {
            if (r.success) {
                //
            }
            return r;
        }));
    }

    public restoreFile(moovFileId:number, moovStepInstanceId:number) {
        return this.mmApiService.restoreFile(moovFileId, moovStepInstanceId).pipe(map((r) => {
            if (r.success) {
                //
            }
            return r;
        }));
    }

    public getMoovExecutionHistory(forceReload: boolean, moovDefinitionId: number) {
        if (forceReload) delete this.moovInstanceExecutionHistoryCache[moovDefinitionId];
        if (!this.authorized) return of([]);
        // Data available
        if (this.moovInstanceExecutionHistoryCache[moovDefinitionId]) return of(this.moovInstanceExecutionHistoryCache[moovDefinitionId]);
        // Request pending
        else if (this.moovInstanceExecutionHistoryObservableCache[moovDefinitionId]) return this.moovInstanceExecutionHistoryObservableCache[moovDefinitionId];
        // New request needed
        else this.moovInstanceExecutionHistoryObservableCache[moovDefinitionId] = this.fetchMoovExecutionHistory(moovDefinitionId);

        return this.moovInstanceExecutionHistoryObservableCache[moovDefinitionId];
    }
    private fetchMoovExecutionHistory(moovDefinitionId: number) {
        return this.mmApiService.getMoovExecutionHistory(moovDefinitionId, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => this.processMoovExecutionHistory(r, moovDefinitionId)), share());
    }
    private processMoovExecutionHistory(r: MoovExecutionHistoryResponse, moovDefinitionId: number) {
        delete this.moovInstanceExecutionHistoryObservableCache[moovDefinitionId];
        let historyArray:MoovInstanceExecutionHistory[] = [];
        let refreshActionItems:boolean =false;
        if (r.success && r.moovInstanceExecutionHistory) {
            r.moovInstanceExecutionHistory?.forEach(dto => {
                var h = MoovInstanceExecutionHistory.mapFromDTO(dto);
                historyArray.push(h);
                if(h.currentStepsStatus==5 || h.waitingForApprovalByUserId) refreshActionItems=true; //suspended status could mean a collision
            });
            if(refreshActionItems) this.getActionItemsCount(true).pipe(take(1)).subscribe();
        }
        else {
            this.handleError('processMoovExecutionHistory', r.message);
        }
        return historyArray;
    }

    public getMoovConfigurationExecutionHistory(moovConfigurationId: number, fromDateTime:Date, toDateTime:Date, statusFilter:number[]) {
        return this.mmApiService.getMoovConfigurationExecutionHistory(moovConfigurationId, fromDateTime, toDateTime, statusFilter, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => this.processMoovConfigurationExecutionHistory(r)), share());
    }
    private processMoovConfigurationExecutionHistory(r: MoovExecutionHistoryResponse) {
        let historyArray:MoovInstanceExecutionHistory[] = [];
        if (r.success && r.moovInstanceExecutionHistory) {
            r.moovInstanceExecutionHistory?.forEach(dto => {
                var h = MoovInstanceExecutionHistory.mapFromDTO(dto);
                historyArray.push(h);
            });
        }
        else {
            this.handleError('processMoovConfigurationExecutionHistory', r.message);
        }
        return historyArray;
    }

    public getProcessingMoovInstances(forceReload: boolean) {
        if (forceReload) this.processingMoovInstancesCache = undefined;
        if (!this.authorized) return of([]);
        // Data available
        if (this.processingMoovInstancesCache) return of(this.processingMoovInstancesCache);
        // Request pending
        else if (this.processingMoovInstancesObservableCache) return this.processingMoovInstancesObservableCache;
        // New request needed
        else this.processingMoovInstancesObservableCache = this.fetchProcessingMoovInstances();

        return this.processingMoovInstancesObservableCache;
    }
    private fetchProcessingMoovInstances() {
        return this.mmApiService.getProcessingMoovInstances(this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => this.processProcessingMoovInstances(r)), share());
    }
    private processProcessingMoovInstances(r: ProcessingMoovInstancesResponse) {
        this.processingMoovInstancesObservableCache = undefined;
        let miaArray: MoovInstanceAbstract[] = [];
        if (r.success) {
            r.moovInstanceAbstractList?.forEach(dto => {
                var mia = MoovInstanceAbstract.mapFromDTO(dto);
                miaArray.push(mia);
            });
            miaArray.sort((a, b) => (b.added.getTime() - a.added.getTime()))
        }
        else {
            this.handleError('processProcessingMoovInstances', r.message);
        }
        return miaArray;
    }

    public getMoovInstance(forceReload: boolean, moovInstanceId: number) {
        if (forceReload) delete this.moovInstancesCache[moovInstanceId];
        if (!this.authorized) return of(undefined);
        // Data available
        if (this.moovInstancesCache[moovInstanceId]) return of(this.moovInstancesCache[moovInstanceId]);
        // Request pending
        else if (this.moovInstancesObservableCache[moovInstanceId]) return this.moovInstancesObservableCache[moovInstanceId];
        // New request needed
        else this.moovInstancesObservableCache[moovInstanceId] = this.fetchMoovInstance(moovInstanceId);

        return this.moovInstancesObservableCache[moovInstanceId];
    }

    private fetchMoovInstance(moovInstanceId: number) {
        return this.mmApiService.getMoovInstance(moovInstanceId, this.authService.entityContextId,this.authService.entityContextType)
        .pipe(
            switchMap((r) => this.processMoovInstance(r)),
            catchError(err => {
                delete this.moovInstancesObservableCache[moovInstanceId];
                return of(undefined);
            }),
       share());
    }

    private processMoovInstance(r: MoovInstanceResponse): Observable<MoovInstance|undefined> {
        if (!(r.success && r.moovInstance)) {
            this.handleError('processMoovInstance', r.message);
            return of(undefined);
        }

        const mi = MoovInstance.mapFromDTO(r.moovInstance);

        return this.getMoovDefinitionById(mi.moovConfiguration.moovDefinitionId, false).pipe(
            take(1),
            tap(md => {
                mi.moovConfiguration.moovDefinition = md!;
                if (this.actionItemsCountCache == undefined || (mi.updated && mi.updated > this.actionItemsCountCache.timestamp && mi.stepInstances.findIndex(msi => msi.approvalId && msi.approvalId.length > 0) > -1)) {
                    this.getActionItemsCount(true).pipe(take(1)).subscribe();
                }
                this.moovInstancesCache[mi.id] = mi;
                delete this.moovInstancesObservableCache[mi.id];
            }),
            map(() => mi)
        );
    }

    public getMoovInstances(forceReload: boolean, moovInstanceIdList: number[]){
        if (!this.authorized) return of([] as MoovInstance[]);
        var idListToLoad:number[] = [];
        var cachedInstances: MoovInstance[] = [];
        moovInstanceIdList.forEach(id => {
            if (forceReload) {
                delete this.moovInstancesCache[id];
            }
            if (!this.moovInstancesCache[id]) {
                idListToLoad.push(id);
            } else {
                cachedInstances.push(this.moovInstancesCache[id]);
            }
        });
        if(idListToLoad.length === 0) {
            return of(cachedInstances);
        }
        return this.fetchMoovInstances(idListToLoad, cachedInstances);
    }

    private fetchMoovInstances(moovInstanceIdList: number[], cachedInstances: MoovInstance[]) {
        return this.mmApiService.getMoovInstances(this.authService.currentUserValue!.id, moovInstanceIdList, this.authService.entityContextId,this.authService.entityContextType)
        .pipe(
            switchMap((r) => this.processMoovInstances(r, cachedInstances)),
            catchError(err => {
                return of([] as MoovInstance[]);
            }),
        share());
    }

    private processMoovInstances(r: MoovInstancesResponse, cachedInstances: MoovInstance[]): Observable<MoovInstance[]> {
        if (!(r.success && r.moovInstanceList)) {
            this.handleError('processMoovInstances', r.message);
            return of(cachedInstances);
        }
        //build each instance once so the object we hydrate, cache and return are the same reference
        const instances = r.moovInstanceList.map(dto => MoovInstance.mapFromDTO(dto));
        const moovDefinitionIds = instances.map(mi => mi.moovConfiguration.moovDefinitionId);
        return this.getMoovDefinitionsById(moovDefinitionIds, false).pipe(
            take(1),
            map(mdArray => {
                instances.forEach(mi => {
                    mi.moovConfiguration.moovDefinition = mdArray.find(md => md.id === mi.moovConfiguration.moovDefinitionId)!;
                    this.moovInstancesCache[mi.id] = mi;
                });
                return [...instances, ...cachedInstances];
            })
        );
    }

    public getMoovInstanceCollision(forceReload: boolean, collisionId: string) {
        if (forceReload) delete this.moovInstanceCollisionsCache[collisionId];
        if (!this.authorized) return of([]);
        // Data available
        if (this.moovInstanceCollisionsCache[collisionId]) return of(this.moovInstanceCollisionsCache[collisionId]);
        // Request pending
        else if (this.moovInstanceCollisionsObservableCache[collisionId]) return this.moovInstanceCollisionsObservableCache[collisionId];
        // New request needed
        else this.moovInstanceCollisionsObservableCache[collisionId] = this.fetchMoovInstanceCollision(collisionId);

        return this.moovInstanceCollisionsObservableCache[collisionId];
    }

    private fetchMoovInstanceCollision(collisionId: string) {
        return this.mmApiService.getMoovInstanceCollision(collisionId, this.authService.entityContextId,this.authService.entityContextType).pipe(switchMap((rArr) => this.processMoovInstanceCollision(rArr, collisionId)), share());
    }

    private processMoovInstanceCollision(rArr: MoovInstanceResponse[], collisionId: string): Observable<MoovInstance[]> {
        const valid = rArr.filter(r => r.success && r.moovInstance);
        if (valid.length === 0) {
            this.moovInstanceCollisionsCache[collisionId] = [];
            delete this.moovInstanceCollisionsObservableCache[collisionId];
            return of([]);
        }
        return forkJoin(valid.map(r => {
            const mi = MoovInstance.mapFromDTO(r.moovInstance!);
            return this.getMoovDefinitionById(mi.moovConfiguration.moovDefinitionId, false).pipe(
                take(1),
                map(md => { mi.moovConfiguration.moovDefinition = md!; return mi; })
            );
        })).pipe(
            tap(miArray => {
                miArray.forEach(mi => { this.moovInstancesCache[mi.id] = mi; });
                this.moovInstanceCollisionsCache[collisionId] = miArray;
                delete this.moovInstanceCollisionsObservableCache[collisionId];
            })
        );
    }

    public getMoovInstanceUpdateTime(moovInstanceId: number) {
        return this.mmApiService.getMoovInstanceUpdateTime(moovInstanceId);
    }

    public getMoovStepInstanceStatus(moovStepInstanceId: number) {
        return this.mmApiService.getMoovStepInstanceStatus(moovStepInstanceId);
    }

    public getMoovFile(moovFileId: number, entityId?: number, entityType?:string) {
        return this.mmApiService.getMoovFile(moovFileId, entityId?entityId:this.authService.entityContextId, entityType?entityType:this.authService.entityContextType);
    }

    public approveMoovStepInstance(msi: MoovStepInstance, moovInstanceId: number) {
        return this.mmApiService.approveMoovStepInstance(this.authService.currentUserValue!.id, msi.id, this.authService.entityContextId, this.authService.entityContextType).pipe(map((r: BasicResponse) => {
            if (r.success) this.processApproveMoovStepInstance(r, msi, moovInstanceId);
            return r;
        }), share());
    }
    private processApproveMoovStepInstance(r: BasicResponse, msi: MoovStepInstance, moovInstanceId: number) {
        delete this.moovInstancesCache[moovInstanceId];
        if (msi.approvalId) {
            this.removeActionItemFromCache(msi.approvalId);
        }
    }

    public executeMoovStepInstance(msi: MoovStepInstance, moovInstanceId: number) {
        return this.mmApiService.executeMoovStepInstance(this.authService.currentUserValue!.id, msi.id, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r: BasicResponse) => {
            if (r.success) this.processExecuteMoovStepInstance(r, msi, moovInstanceId);
            return r;
        }), share());
    }
    private processExecuteMoovStepInstance(r: BasicResponse, msi: MoovStepInstance, moovInstanceId: number) {
        delete this.moovInstancesCache[moovInstanceId];
    }

    public runFailedMoovStepInstance(msi: MoovStepInstance, moovInstanceId: number) {
        return this.mmApiService.runFailedMoovStepInstance(this.authService.currentUserValue!.id, msi.id, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r: BasicResponse) => {
            if (r.success) this.processRunFailedMoovStepInstance(r, msi, moovInstanceId);
            return r;
        }), share());
    }
    private processRunFailedMoovStepInstance(r: BasicResponse, msi: MoovStepInstance, moovInstanceId: number) {
        delete this.moovInstancesCache[moovInstanceId]; //clear cache and force reload
        if (r.reference) {
            //collision!
            var collisionId = r.reference;
            this.getActionItemsCount(true).pipe(take(1)).subscribe();
            if (this.moovInstanceCollisionsCache[collisionId]) delete this.moovInstanceCollisionsCache[collisionId];
        }
    }

    public suspendMoovInstance(mi: MoovInstance) {
        return this.mmApiService.suspendMoovInstance(this.authService.currentUserValue!.id, mi.id, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r: BasicResponse) => {
            if (r.success) this.processSuspendMoovInstance(r, mi);
            return r;
        }), share());
    }
    private processSuspendMoovInstance(r: BasicResponse, mi: MoovInstance) {
        delete this.moovInstancesCache[mi.id];
    }

    public resumeMoovInstance(mi: MoovInstance) {
        return this.mmApiService.resumeMoovInstance(this.authService.currentUserValue!.id, mi.id, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r: BasicResponse) => {
            if (r.success) this.processResumeMoovInstance(r, mi);
            return r;
        }), share());
    }
    private processResumeMoovInstance(r: BasicResponse, mi: MoovInstance) {
        delete this.moovInstancesCache[mi.id]; //force reload as new steps might have been created
    }

    public startMoovInstance(mi: MoovInstance) {
        return this.mmApiService.startMoovInstance(this.authService.currentUserValue!.id, mi.id, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r: BasicResponse) => {
            if (r.success) this.processStartMoovInstance(r, mi);
            return r;
        }), share());
    }
    private processStartMoovInstance(r: BasicResponse, mi: MoovInstance) {
        delete this.moovInstancesCache[mi.id]; //force reload as new steps might have been created
    }

    public deleteMoovInstance(mi: MoovInstance) {
        return this.mmApiService.deleteMoovInstance(this.authService.currentUserValue!.id, mi.id, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r: BasicResponse) => {
            if (r.success) this.processDeleteMoovInstance(r, mi);
            return r;
        }), share());
    }
    private processDeleteMoovInstance(r: BasicResponse, mi: MoovInstance) {
        mi.status = 4;
    }

    public resolveMoovInstanceCollision(collisionId: string) {
        return this.mmApiService.resolveMoovInstanceCollision(this.authService.currentUserValue!.id, collisionId, this.authService.entityContextId,this.authService.entityContextType).pipe(map((rArr: BasicResponse[]) => {
            return this.processResolveMoovInstanceCollision(rArr, collisionId);
        }), share());
    }

    private processResolveMoovInstanceCollision(rArr: BasicResponse[], collisionId: string) {
        //need to loop through BasicResponse items:
        //if success then mark the related moov instance (longCode) as resumed and set status to code
        //if no success then there was an exception on the related moov instance (longCode); shouldn't happen. show message to user
        let response = new BasicResponse();
        if (rArr?.length > 0) {
            let index = rArr.findIndex(r => !r.success);
            response.success = index == -1;
            if (index > -1) response.message = rArr[index].message;
            else {
                //all good remove actionItem if loaded
                if (this.actionItemsCache) {
                    let aiIndex = this.actionItemsCache.findIndex(ai => ai.actionReferenceId == collisionId);
                    if (index > -1) this.actionItemsCache.splice(aiIndex, 1);
                }

            }
            rArr.forEach(r => {
                if (r.success && r.longCode) {
                    //remove instance from collision
                    const mi = this.moovInstancesCache[r.longCode];
                    if(mi){
                        mi.status = r.code!;
                        this.moovInstancesCache[r.longCode].collisionId = undefined;
                    }
                }
                else {
                    //nothing for now
                }
            });
            this.getActionItemsCount(true).pipe(take(1)).subscribe();
        }
        else {
            response.success = false;
            response.message = "Collision does not exist anymore";
        }
        return response;
    }
    //#endregion

    //#region moov variables handling
    public getMoovVariables(forceReload: boolean) {
        if (forceReload) this.moovVariablesCache = undefined;
        if (!this.authorized) return of([]);
        // Data available
        if (this.moovVariablesCache) return of(this.moovVariablesCache);
        // Request pending
        else if (this.moovVariablesObservableCache) return this.moovVariablesObservableCache;
        // New request needed
        else this.moovVariablesObservableCache = this.fetchMoovVariables();

        return this.moovVariablesObservableCache;
    }

    private fetchMoovVariables() {
        return this.mmApiService.getMoovVariables(this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => this.processMoovVariables(r)), share());
    }

    private processMoovVariables(r: MoovVariableResponse) {
        this.moovVariablesObservableCache = undefined;
        this.moovVariablesCache = new Array<MoovVariable>(0);
        if (r.success) {
            r.moovVariables?.forEach(dto => {
                this.moovVariablesCache!.push(this.processMoovVariableDTO(dto));
            });
        }
        else {
            this.handleError('processMoovVariables', r.message);
        }
        return this.moovVariablesCache;
    }

    private processMoovVariableDTO(dto:MoovVariableDTO):MoovVariable{
        return MoovVariable.mapFromDTO(dto);
    }

    public createMoovVariable(mv: MoovVariable) {
        return this.mmApiService.createMoovVariable(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, mv).pipe(map((r: MoovVariableResponse) => {
            if (r.success) this.processCreateMoovVariable(r);
            return r;
        }), share());
    }
    private processCreateMoovVariable(r: MoovVariableResponse) {
        if(r.moovVariables && r.moovVariables.length>0){
            this.moovVariablesCache!.push(this.processMoovVariableDTO(r.moovVariables[0]));
        }
        else{
            this.handleError('processCreateMoovVariable', r.message);
        }
    }

    public updateMoovVariable(mv: MoovVariable) {
        return this.mmApiService.updateMoovVariable(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, mv).pipe(map((r: MoovVariableResponse) => {
            if (r.success) this.processUpdateMoovVariable(r);
            return r;
        }), share());
    }
    private processUpdateMoovVariable(r: MoovVariableResponse) {
        if(r.moovVariables && r.moovVariables.length>0){
            var v = this.processMoovVariableDTO(r.moovVariables[0]);
            let index = this.moovVariablesCache!.findIndex(mv=>mv.id==v.id);
            if(index>-1){
                this.moovVariablesCache![index]=v;
            }
            else{
                this.moovVariablesCache!.push(v);
            }
        }
        else{
            this.handleError(typeof(r), r.message);
        }
    }

    public deleteMoovVariable(mv: MoovVariable) {
        return this.mmApiService.deleteMoovVariable(this.authService.currentUserValue!.id, this.authService.entityContextId,this.authService.entityContextType, mv.id).pipe(map((r: BasicResponse) => {
            if (r.success) this.processDeleteMoovVariable(mv);
            return r;
        }), share());
    }
    private processDeleteMoovVariable(mv: MoovVariable) {
        let index = this.moovVariablesCache!.indexOf(mv);
        this.moovVariablesCache!.splice(index, 1);
    }
    //#endregion

    //#region action items
    public getActionItemsCount(forceRefresh: boolean) {
        let refreshCache = true;
        if (!forceRefresh && this.actionItemsCountCache) {
            let minutes = Math.abs(this.actionItemsCountCache.timestamp.getTime() - new Date().getTime()) / (1000 * 60) % 60;
            if (minutes < 10) {
                refreshCache = false;
            }
        }
        if (refreshCache) {
            if (this.actionItemsCountObservableCache) return this.actionItemsCountObservableCache;
            else {
                return this.actionItemsCountObservableCache = this.mmApiService.getActionItemsCount(this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => {
                    this.actionItemsCountObservableCache = undefined;
                    this.updateActionItemsCount(r);
                    return this.actionItemsCountCache;
                }), share());
            }
        }
        else {
            return of(this.actionItemsCountCache);
        }
    }

    private updateActionItemsCount(count?: number) {
        if (count != undefined) {
            if (!this.actionItemsCountCache || this.actionItemsCountCache.count != count) {
                this.actionItemsCountCache = { count: count, timestamp: new Date() };
                this.actionItemCountChangedSubject.next(this.actionItemsCountCache);
            }
            else {
                this.actionItemsCountCache.timestamp = new Date();
            }
        }
        else {
            if (this.actionItemsCache) {
                if (this.actionItemsCache.length != this.actionItemsCountCache.count) {
                    this.actionItemsCountCache = { count: this.actionItemsCache.length, timestamp: new Date() };
                    this.actionItemCountChangedSubject.next(this.actionItemsCountCache);
                }
            }
            else {
                if (!this.actionItemsCountCache) {
                    this.actionItemsCountCache = { count: 0, timestamp: new Date() };
                    this.actionItemCountChangedSubject.next(this.actionItemsCountCache);
                }
                else {
                    this.actionItemsCountCache.count--;
                    if (this.actionItemsCountCache.count < 0) this.actionItemsCountCache.count = 0;
                    this.actionItemsCountCache.timestamp = new Date();
                    this.actionItemCountChangedSubject.next(this.actionItemsCountCache);
                }
            }
        }
        return this.actionItemsCountCache;
    }

    public getActionItems(forceReload: boolean) {
        if (forceReload) this.actionItemsCache = undefined;
        if (!this.authorized) return of([]);
        // Data available
        if (this.actionItemsCache) return of(this.actionItemsCache);
        // Request pending
        else if (this.actionItemsObservableCache) return this.actionItemsObservableCache;
        // New request needed
        else this.actionItemsObservableCache = this.fetchActionItems();

        return this.actionItemsObservableCache;
    }

    private fetchActionItems() {
        return this.mmApiService.getActionItems(this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => this.processActionItems(r)), share());
    }

    private processActionItems(r: ActionItemResponse) {
        this.actionItemsObservableCache = undefined;
        this.actionItemsCache = new Array<ActionItem>(0);
        if (r.success) {
            r.actionItemList?.forEach(dto => {
                var ai = ActionItem.mapFromDTO(dto);
                this.actionItemsCache!.push(ai);
            });
        }
        else {
            this.handleError(typeof (r), r.message);
        }
        this.updateActionItemsCount(this.actionItemsCache.length);
        return this.actionItemsCache;
    }

    public actionItemAction(requestorId: number, requestorEntityId: number, requestorEntityType: string, actionItem: ActionItem, typeOfActionTaken: string, remarkForActionTaken: string) {
        return this.mmApiService.actionItemAction(requestorId, requestorEntityId, requestorEntityType, actionItem.id, typeOfActionTaken, remarkForActionTaken).pipe(map((r: BasicResponse) => {
            if (r.success) this.processActionItemAction(actionItem, requestorId, typeOfActionTaken, remarkForActionTaken);
            return r;
        }), share());
    }
    private processActionItemAction(actionItem: ActionItem, requestorId: number, typeOfActionTaken: string, remarkForActionTaken: string) {
        const index = this.actionItemsCache!.indexOf(actionItem);
        if(index>-1) this.actionItemsCache!.splice(index, 1);
    }

    private removeActionItemFromCache(actionItemId: string) {
        if (this.actionItemsCache) {
            const index = this.actionItemsCache.findIndex(ai => ai.actionReferenceId == actionItemId);
            if (index >-1) this.actionItemsCache.splice(index, 1);
        }
        this.updateActionItemsCount();
    }
    //#endregion

    //#region received files
    public getReceivedFile(receivedFileId: number) {
        return this.mmApiService.getReceivedFile(receivedFileId, this.authService.entityContextId, this.authService.entityContextType);
    }

    public getReceivedFiles(fromDateTime: Date, toDateTime: Date, statusFilter: string) {
        if (!this.authorized) return of([]);
        // Request pending
        if (this.receivedFilesObservableCache) return this.receivedFilesObservableCache;
        // New request needed
        else this.receivedFilesObservableCache = this.fetchReceivedFiles(fromDateTime, toDateTime, statusFilter);
        return this.receivedFilesObservableCache;
    }

    private fetchReceivedFiles(fromDateTime: Date, toDateTime: Date, statusFilter: string) {
        return this.mmApiService.getReceivedFiles(fromDateTime, toDateTime, statusFilter, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => this.processReceivedFiles(r)), share());
    }

    private processReceivedFiles(r: ReceivedFileResponse) {
        this.receivedFilesObservableCache = undefined;
        let receivedFiles = new Array<ReceivedFile>(0);
        if (r.success) {
            r.receivedFileList?.forEach(dto => {
                var rf = ReceivedFile.mapFromDTO(dto);
                receivedFiles.push(rf);
            });
        }
        else {
            this.handleError('processReceivedFiles', r.message);
        }
        return receivedFiles;
    }
    //#endregion

    //#region executed moov instances
    public getExecutedMoovInstances(fromDateTime: Date, toDateTime: Date, statusFilter: number[]) {
        if (!this.authorized) return of([]);
        // Request pending
        if (this.executedMoovInstancesObservableCache) return this.executedMoovInstancesObservableCache;
        // New request needed
        else this.executedMoovInstancesObservableCache = this.fetchExecutedMoovInstances(fromDateTime, toDateTime, statusFilter);
        return this.executedMoovInstancesObservableCache;
    }

    private fetchExecutedMoovInstances(fromDateTime: Date, toDateTime: Date, statusFilter: number[]) {
        return this.mmApiService.getExecutedMoovInstances(fromDateTime, toDateTime, statusFilter, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r) => this.processExecutedMoovInstances(r)), share());
    }

    private processExecutedMoovInstances(r: ExecutedMoovInstancesResponse) {
        this.executedMoovInstancesObservableCache = undefined;
        let executedMoovInstances = new Array<ExecutedMoovInstance>(0);
        if (r.success) {
            r.executedMoovInstanceList?.forEach(dto => {
                var emi = ExecutedMoovInstance.mapFromDTO(dto);
                executedMoovInstances.push(emi);
            });
        }
        else {
            this.handleError('processExecutedMoovInstances', r.message);
        }
        return executedMoovInstances;
    }
    //#endregion

    //#region billable moov data
    public getBillableMoovData(fromDateTime: Date, toDateTime: Date, customerId?: number) {
        if (!this.authorized) return of([]);
        // Request pending
        if (this.billableMoovDataObservableCache) return this.billableMoovDataObservableCache;
        // New request needed
        else this.billableMoovDataObservableCache = this.fetchBillableMoovData(fromDateTime, toDateTime, customerId);
        return this.billableMoovDataObservableCache;
    }

    public fetchBillableMoovData(fromDateTime: Date, toDateTime: Date, customerId?: number) {
        const entityType = this.authService.entityContextType?this.authService.entityContextType:(customerId?"C":undefined);
        const entityId = this.authService.entityContextId?this.authService.entityContextId:customerId;
        return this.mmApiService.getBillableMoovData(fromDateTime, toDateTime, entityId, entityType).pipe(map((r) => this.processBillableMoovData(r)), share());
    }

    public processBillableMoovData(r: BillableMoovDataResponse) {
        this.billableMoovDataObservableCache = undefined;
        let billableMoovData = new Array<BillableMoovData>(0);
        if (r.success) {
            r.billableMoovDataList?.forEach(dto => {
                var bmd = BillableMoovData.mapFromDTO(dto);
                billableMoovData.push(bmd);
            });
        }
        else {
            this.handleError('processBillableMoovData', r.message);
        }
        return billableMoovData;
    }
    //#endregion

    //#region search file
    public searchFiles(fileName: string, fromDateTime: Date, toDateTime: Date, startsWith: boolean) {
        if (!this.authorized) return of([]);
        // Request pending
        if (this.searchFileObservableCache) return this.searchFileObservableCache;
        // New request needed
        else this.searchFileObservableCache = this.fetchSearchFiles(fileName, startsWith, fromDateTime, toDateTime);
        return this.searchFileObservableCache;
    }

    public fetchSearchFiles(fileName: string, startsWith: boolean, fromDateTime: Date, toDateTime: Date) {
        return this.mmApiService.searchFiles(fileName, startsWith, fromDateTime, toDateTime).pipe(map((r) => this.processSearchFiles(r)), share());
    }

    public processSearchFiles(r: SearchFileResponse) {
        this.searchFileObservableCache = undefined;
        let searchFiles = new Array<SearchFile>(0);
        if (r.success) {
            r.moovInstanceFileList.forEach(dto => {
                var sf = SearchFile.mapFromDTO(dto);
                searchFiles.push(sf);
            });
        }
        else {
            this.handleError('processSearchFiles', r.message);
        }
        return searchFiles;
    }
    //#endregion

    //#region premier services
    public createPremierServiceSchedule(pss:PremierServiceSchedule):Observable<any>{
        let requestorId=this.authService.currentUserValue!.id;
        return this.mmApiService.createPremierServiceSchedule(pss, requestorId).pipe(map((r)=>this.processCreatePremierServiceSchedule(pss,r)));
    }

    private processCreatePremierServiceSchedule(pss:PremierServiceSchedule,r:BasicResponse){
        if(r.success){
           //nothing for now 
        }
        return r;
    }

    public getPremierServiceExecutions(fromDateTime: Date, toDateTime: Date, customerId?: number){
        if(!this.authorized) of([]);
        //Request pending
        if(this.premierServiceExecutionsObservableCache) return this.premierServiceExecutionsObservableCache;
        //New request needed
        else this.premierServiceExecutionsObservableCache = this.fetchPremierServiceExecutions(fromDateTime, toDateTime, customerId);
        return this.premierServiceExecutionsObservableCache;
    }

    public fetchPremierServiceExecutions(fromDateTime:Date, toDateTime:Date, customerId?:number){
        return this.mmApiService.getPremierServiceExecutions(fromDateTime, toDateTime, this.authService.entityContextId, this.authService.entityContextType).pipe(map((r)=>this.processPremierServiceExecutions(r)), share());
    }

    private processPremierServiceExecutions(r:PremierServiceExecutionsResponse){
        this.premierServiceExecutionsObservableCache=undefined;
        let premierServiceExecutions=new Array<PremierServiceExecution>(0);
        if(r.success){
            r.premierServiceExecutionList?.forEach(dto =>{
                var pse = PremierServiceExecution.mapFromDTO(dto);
                premierServiceExecutions.push(pse);
            });
        }
        else {
            this.handleError('processPremierServiceExecutions', r.message);
        }
        return premierServiceExecutions;
    }

    public getScheduledPremierServices(fromDateTime: Date, toDateTime: Date, customerId?: number){
        if(!this.authorized) return of([]);
        //Request pending
        if(this.scheduledPremierServicesObservableCache) return this.scheduledPremierServicesObservableCache;
        //New request needed
        else this.scheduledPremierServicesObservableCache = this.fetchScheduledPremierServices(fromDateTime, toDateTime, customerId);
        return this.scheduledPremierServicesObservableCache;
    }

    public fetchScheduledPremierServices(fromDateTime:Date, toDateTime:Date, customerId?:number){
        return this.mmApiService.getScheduledPremierServices(fromDateTime, toDateTime, this.authService.entityContextId,this.authService.entityContextType).pipe(map((r)=>this.processScheduledPremierServices(r)), share());
    }

    private processScheduledPremierServices(r:ScheduledPremierServicesResponse){
        this.scheduledPremierServicesObservableCache=undefined;
        let scheduledPremierServices=new Array<ScheduledPremierService>(0);
        if(r.success){
            r.scheduledPremierServiceList.forEach(dto =>{
                var sps = ScheduledPremierService.mapFromDTO(dto);
                sps.clientName
                scheduledPremierServices.push(sps);
            });
        }
        else {
            this.handleError('processScheduledPremierServices', r.message);
        }
        return scheduledPremierServices;
    }
    //#endregion

    //#region qbo
    public deleteQBEntriesForMoovStepInstance(moovStepInstance:MoovStepInstance, customerId: number, clientId:number){
        let requestorId=this.authService.currentUserValue!.id;
        let requestorCustomerId=customerId?customerId:this.authService.customerContext?.id;
        return this.mmApiService.deleteQBEntriesForMoovStepInstance(moovStepInstance.id, requestorId, requestorCustomerId!, clientId).pipe(map((r)=>this.processDeleteQBEntries(r, moovStepInstance)), share());
    }

    private processDeleteQBEntries(r:BasicResponse,moovStepInstance:MoovStepInstance){
        if(r.success){
            moovStepInstance.undoInProcess=true;
        }
        else {
            this.handleError('processDeleteQBEntries', r.message);
        }
        return r;
    }
    //#endregion

    //#region xero
    public deleteXeroEntriesForMoovStepInstance(moovStepInstance:MoovStepInstance, customerId: number, clientId:number){
        let requestorId=this.authService.currentUserValue!.id;
        let requestorCustomerId=customerId?customerId:this.authService.customerContext?.id;
        return this.mmApiService.deleteXeroEntriesForMoovStepInstance(moovStepInstance.id, requestorId, requestorCustomerId!, clientId).pipe(map((r)=>this.processDeleteXeroEntries(r, moovStepInstance)), share());
    }

    private processDeleteXeroEntries(r:BasicResponse,moovStepInstance:MoovStepInstance){
        if(r.success){
            moovStepInstance.undoInProcess=true;
        }
        else {
            this.handleError('processDeleteXeroEntries', r.message);
        }
        return r;
    }
    //#endregion

    //#region microsoft
    public deleteMicrosoftCalendarEntriesForMoovStepInstance(moovStepInstance:MoovStepInstance, customerId: number, clientId:number){
        let requestorId=this.authService.currentUserValue!.id;
        let requestorCustomerId=customerId?customerId:this.authService.customerContext?.id;
        return this.mmApiService.deleteMicrosoftCalendarEntriesForMoovStepInstance(moovStepInstance.id, requestorId, requestorCustomerId!, clientId).pipe(map((r)=>this.processDeleteMicrosoftCalendarEntries(r, moovStepInstance)), share());
    }

    private processDeleteMicrosoftCalendarEntries(r:BasicResponse,moovStepInstance:MoovStepInstance){
        if(r.success){
            moovStepInstance.undoInProcess=true;
        }
        else {
            this.handleError('processDeleteMicrosoftCalendarEntries', r.message);
        }
        return r;
    }
    //#endregion

    //#region time zone stuff
    public getTimeZoneById(id:string){
        return this.mmApiService.getTimeZoneById(id).pipe(map((r)=>{return TimeZone.mapFromDTO(r)}));
    }
    public getSupportedTimeZones(){
        return this.mmApiService.getSupportedTimeZones().pipe(map((r)=>{
            let timeZoneArray = new Array<TimeZone>(0);
            if(!(r instanceof BasicResponse)){
                r.forEach((tz:TimeZoneDTO)=>{
                    let zone = TimeZone.mapFromDTO(tz);
                    timeZoneArray.push(zone);
                });
            }
            else{
                //BasicResponse
                this.logger.log(r);
            }
            return timeZoneArray;
        }));
    }
    //#endregion

    private handleError(source: string, message: string, navigateToError: boolean = false) {
        const stack = new Error().stack;
        const timestamp = new Date().toISOString();
        console.error(`[${timestamp}]`,`[handleError] source=${source} message=${message}`, stack);
        if (navigateToError) {
            this.router.navigate(['/error']);
        }
    }

    private handleCustomerError(data: any) {
        this.authService.brandingDataCache = data;
        this.router.navigate(['/customer-error']);
    }
}